import React, { useState, useEffect } from 'react';
import { StandardModal, StandardButton, MODAL_SECTIONS } from './Modal';

const PermissionRequestDialog = ({ 
  isOpen, 
  onClose, 
  onAccept, 
  permission, 
  isRequesting = false 
}) => {
  const [showDetails, setShowDetails] = useState(false);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isRequesting) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isRequesting, onClose]);

  const getPermissionInfo = (perm) => {
    const permissions = {
      camera: {
        icon: '📷',
        title: 'Camera Access',
        description: 'Required for barcode scanning and photo capture',
        details: 'This permission allows ACCTOO to access your camera for scanning barcodes, capturing product photos, and taking pictures for invoices.'
      },
      storage: {
        icon: '💾',
        title: 'Storage Access',
        description: 'Required for file uploads and exports',
        details: 'This permission allows ACCTOO to save files, export data, and manage documents on your device.'
      },
      location: {
        icon: '📍',
        title: 'Location Access',
        description: 'Optional for location-based features',
        details: 'This permission allows ACCTOO to access your location for tracking device usage and providing location-based features.'
      },
      notifications: {
        icon: '🔔',
        title: 'Notification Access',
        description: 'Required for alerts and reminders',
        details: 'This permission allows ACCTOO to send you notifications about important updates, reminders, and alerts.'
      },
      contacts: {
        icon: '📞',
        title: 'Contacts Access',
        description: 'Optional for contact synchronization',
        details: 'This permission allows ACCTOO to access your contacts for easier customer management and contact synchronization.'
      },
      microphone: {
        icon: '🎤',
        title: 'Microphone Access',
        description: 'Optional for voice features',
        details: 'This permission allows ACCTOO to access your microphone for voice notes and audio features.'
      },
      calendar: {
        icon: '📅',
        title: 'Calendar Access',
        description: 'Optional for event scheduling',
        details: 'This permission allows ACCTOO to access your calendar for scheduling reminders and events.'
      },
      bluetooth: {
        icon: '🔵',
        title: 'Bluetooth Access',
        description: 'Optional for device connectivity',
        details: 'This permission allows ACCTOO to connect to Bluetooth devices like printers and scanners.'
      }
    };
    
    return permissions[perm] || {
      icon: '⚙️',
      title: 'Permission Access',
      description: 'Required for app functionality',
      details: 'This permission is required for ACCTOO to function properly.'
    };
  };

  const permissionInfo = getPermissionInfo(permission);

  if (!isOpen) return null;

  return (
    <StandardModal section={MODAL_SECTIONS.SETTINGS} 
      isOpen={isOpen} 
      onClose={onClose} 
      title={`${permissionInfo.icon} ${permissionInfo.title}`}
      size="md"
      showCloseButton={!isRequesting}
    >
      <div className="space-y-4">
        <div className="text-center">
          <div className="text-6xl mb-4">{permissionInfo.icon}</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {permissionInfo.title}
          </h3>
          <p className="text-gray-600 mb-4">
            {permissionInfo.description}
          </p>
        </div>

        {isRequesting && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              <p className="text-sm text-blue-800">
                Requesting permission from your device...
              </p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full text-left text-sm text-blue-600 hover:text-blue-800 underline"
          >
            {showDetails ? 'Hide' : 'Show'} details about this permission
          </button>
          
          {showDetails && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <p className="text-sm text-gray-700">
                {permissionInfo.details}
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-4">
          <StandardButton 
            onClick={onClose} 
            variant="secondary" 
            className="flex-1"
            disabled={isRequesting}
          >
            Cancel
          </StandardButton>
          <StandardButton 
            onClick={onAccept} 
            className="flex-1"
            disabled={isRequesting}
          >
            {isRequesting ? 'Requesting...' : 'Allow'}
          </StandardButton>
        </div>
        
        <div className="text-center pt-2">
          <button
            onClick={onClose}
            disabled={isRequesting}
            className="text-sm text-gray-500 hover:text-gray-700 underline disabled:opacity-50"
          >
            Skip for now
          </button>
        </div>
      </div>
    </StandardModal>
  );
};

export default PermissionRequestDialog;
